import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  { q: "Vad är en kundcase-film?", a: "En film där er kund berättar konkret om resultat av att jobba med er. Inte ett testimonial-citat — en historia med problem, lösning och mätbart utfall." },
  { q: "Vad kostar en kundcase-film?", a: "1-dags inspelning hos kunden + 1 färdig film + 3–4 paid-cuts: 50–110 kkr. Större produktion med flera roller i caset: 120–250 kkr." },
  { q: "Hur lång ska en kundcase-film vara?", a: "90 sekunder för sajt och säljmöten. 30 sekunder för paid social. 15 sekunder för \"hooks\". Vi levererar alla tre ur samma inspelning." },
  { q: "Vem leder intervjun?", a: "Vår regissör — utbildad i att få äkta svar utan att lägga ord i munnen på era kunder. Inga manuskort." },
];

export const Route = createFileRoute("/kundcase-film")({
  head: () => ({
    meta: [
      { title: "Kundcase-film — så får ni kunden att säga ja och ge siffror" },
      { name: "description", content: "Kundcase-film från Fenny. Riktiga kunder, riktiga resultat — film för säljmöten, sajt och paid social. Produktion från brief till leverans inom 3 veckor." },
      { property: "og:title", content: "Kundcase-film — Fenny" },
      { property: "og:description", content: "Kundcase-film som faktiskt säljer — inte testimonial-citat." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/kundcase-film" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/kundcase-film" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/kundcase-film", name: "Kundcase-film — så får ni kunden att säga ja och ge siffror", description: "Kundcase-film från Fenny. Riktiga kunder, riktiga resultat — film för säljmöten, sajt och paid social. Produktion från brief till leverans inom 3 veckor."})) },
      { type: "application/ld+json", children: JSON.stringify(serviceSchema({ name: "Kundcase-film", path: "/kundcase-film", serviceType: "Kundcase och testimonial-film", description: "Kundcase-film med riktiga kunder och mätbara resultat — för sajt, säljmöten och paid social." })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Kundcase-film", path: "/kundcase-film" }])) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      eyebrow="Kundcase-film"
      title={<>Kundcase som faktiskt säljer — inte testimonial som dör i en footer.</>}
      lede="Fenny producerar kundcase-film med riktiga kunder och mätbara resultat. Vi regisserar fram historien — problem, lösning, utfall — i en intervjuform som inte känns plastig. Levereras i 90, 30 och 15 sekunder för sajt, säljmöten och paid social."
      tldr={<p>En kundcase-film (testimonial) är <strong>60–180 sek</strong>, bygger trovärdighet och konverterar i säljprocessen. En intervju, 2–3 cutaways, klipp och versionering till sajt, LinkedIn och sälj. Pris: <strong>25 000–60 000 kr</strong> per case, ofta paketerat 3-pack eller löpande via Pulse.</p>}
      ctaPrimary={{ label: "Boka möte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="kundcase-film"
      relatedLinksFor="/kundcase-film"
      trust={[
        { value: "25–60 kkr", label: "pris per case" },
        { value: "3 v", label: "brief till leverans" },
        { value: "4", label: "format ur samma intervju" },
      ]}
      pricing={{
        heading: "Vad en kundcase-film kostar",
        intro: "Riktpriser 2026. Tre case i följd är den vanligaste beställningen och sänker styckpriset märkbart.",
        rows: [
          { format: "Ett kundcase", scope: "Halvdag hos kunden, 90 sek + 30 sek + 15 sek + quote-grafik", price: "25 000–60 000 kr", lead: "3 veckor" },
          { format: "Case-paket om tre", scope: "3 inspelningar, samma format och grafisk mall", price: "65 000–140 000 kr", lead: "5–6 veckor" },
          { format: "Löpande via Pulse", scope: "Ett nytt case i månaden inom abonnemanget", price: "från 14 995 kr/mån", lead: "första klipp 10–14 dagar" },
        ],
        footnote: "Exkl. moms. Resa inom Sverige ingår i Pulse; för enskilda projekt specificeras resa i offerten.",
      }}
      objections={{
        heading: "Det som brukar stoppa case-filmer",
        items: [
          { objection: "Våra kunder får inte prata om oss.", answer: <>Vanligt i offentlig sektor och finans. Lösningen är ofta en anonymiserad version: roll istället för namn, generisk miljö och siffror godkända av kundens kommunikationsavdelning.</> },
          { objection: "Vi har inga siffror att visa.", answer: <>Då bygger vi på förändring i vardagen istället — vad som tog tid förut och vad som gör det nu. Konkret beteende slår vaga procenttal.</> },
          { objection: "Kunden har inte tid.", answer: <>Vi behöver 90 minuter på plats, inte en hel dag. Frågorna skickas i förväg och vi filmar hos kunden så de slipper resa.</> },
        ],
      }}
      riskReducers
      leadMagnet={{
        slug: "brief-mall",
        title: "Ladda ner: brief-mallen som inte slösar tid",
        description: "1 sida, 7 fält som styr utfallet, plus ett ifyllt exempel. Använd den innan ni tar in offerter.",
      }}
      bullets={[
        { title: "Story, inte citat", body: "Vi vill ha hela bågen: hur var vardagen innan, vad förändrades, vad kostar det att inte ha er produkt." },
        { title: "Mätbara siffror", body: "Vi briefar kunden i förväg och hjälper er hämta in konkreta siffror — annars dör hela poängen." },
        { title: "Säljteamet skickar", body: "Filmen designas för att skickas av AE/CSM som länk i mejl, inte bara för att fly i bakgrunden av sajten." },
      ]}
      sections={[
        { id: "process", heading: "Så går vi tillväga", body: <p>Vecka 1: vi briefar er och kunden, sätter en story-arc och bekräftar de siffror som ska med. Vecka 2: inspelning hos kunden, 1 dag. Vecka 3: klipp, versionering och leverans i delad mapp.</p> },
        { id: "format", heading: "Vilka format levererar vi?", body: <p>90-sekunders huvudversion för sajt och säljmöten. 30 sekunder för LinkedIn/YouTube. 15 sekunder \"hook\" för paid. Plus en quote-grafik för slides. Alla format ur samma intervju.</p> },
        { id: "anvandning", heading: "Var används kundcase-film?", body: <p>På sajt under \"Cases\". I sales-deck efter discovery. I outbound-mejl (\"se hur Företag X gjorde\"). I paid social mot lookalikes. I årsredovisning och investerarmaterial.</p> },
      ]}
      faq={FAQ}
      caseLimit={3}
      closing={{ title: "Vilken kund ska bli ert nästa case?", body: "30 minuter där vi listar era starkaste kandidater och vad de behöver för att tacka ja.", primary: { label: "Boka möte", to: "/kontakt" }, secondary: { label: "Räkna pris", to: "/priser" } }}
    />
  );
}
